import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import UserNavbar from "./UserNavbar";
import { useNavigate } from "react-router-dom";
import {
  Legend,
  Tooltip,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ScatterChart,
  ZAxis,
  Scatter,
  Bar,
  BarChart,
} from "recharts";
import { useDashboard } from "../contexts/DashboardContext";

const Compliance = () => {
  const navigate = useNavigate();
  const token = Cookies.get("token");
  const { setActiveNav } = useDashboard();
  const [complianceTab, setComplianceTab] = useState(1);
  const [complianceData, setComplianceData] = useState([]);
  const [lastComplianceData, setLastComplianceData] = useState([]);
  const [complianceType, setComplianceType] = useState("pci_dss");
  const role = Cookies.get("role");

  const getTopAlertsDataByComplianceType = (complianceType) => {
    return axios.get(
      `/api/system/systems/compliance/topalerts/${complianceType}`,
      {
        params: {
          hours: 24,
        },
        headers: {
          "x-auth-token": token,
        },
      }
    );
  };

  const getLastAlertsDataByComplianceType = (complianceType) => {
    return axios.get(
      `/api/system/systems/compliance/lastalerts/${complianceType}`,
      {
        params: {
          hours: 24,
        },
        headers: {
          "x-auth-token": token,
        },
      }
    );
  };

  useEffect(() => {
    if (!token) {
      navigate("/signin");
    } else {
      axios
        .get("/api/token/isLoggedIn", {
          headers: {
            "x-auth-token": token,
          },
        })
        .then((res) => console.log(res.data))
        .catch(() => navigate("/signin"));
    }
  }, []);

  useEffect(() => {
    if (role === "3") {
      getTopAlertsDataByComplianceType(complianceType).then((res) => {
        setComplianceData(res.data);
      });
    }
  }, [complianceType]);

  useEffect(() => {
    if (role === "3") {
      getLastAlertsDataByComplianceType(complianceType).then((res) => {
        const agents = new Set();
        res.data.forEach((item) => {
          Object.keys(item).forEach((key) => {
            if (key !== "time") agents.add(key);
          });
        });

        const agentsArray = Array.from(agents);

        const normalizedData = res.data.map((item) => {
          agentsArray.forEach((agentName) => {
            if (!item.hasOwnProperty(agentName)) {
              item[agentName] = null;
            }
          });
          return item;
        });

        setLastComplianceData(normalizedData);
      });
    }
  }, [complianceType]);

  useEffect(() => {
    setActiveNav("compliance");
  }, []);

  const showComplianceTabs = () => {
    return (
      <div className="flex justify-center items-center mt-6 xl:mt-0 mb-12">
        <div
          className={`px-6 py-2 ${
            complianceTab === 1 ? "bg-[#05123A] text-white" : "bg-gray-300"
          } cursor-pointer`}
          onClick={() => {
            setComplianceType("pci_dss");
            setComplianceTab(1);
          }}
        >
          PCI-DSS
        </div>
        <div
          className={`px-6 py-2 ${
            complianceTab === 2 ? "bg-[#05123A] text-white" : "bg-gray-300"
          } cursor-pointer`}
          onClick={() => {
            setComplianceType("gdpr");
            setComplianceTab(2);
          }}
        >
          GDPR
        </div>
        <div
          className={`px-6 py-2 ${
            complianceTab === 3 ? "bg-[#05123A] text-white" : "bg-gray-300"
          } cursor-pointer`}
          onClick={() => {
            setComplianceType("hipaa");
            setComplianceTab(3);
          }}
        >
          HIPAA
        </div>
        <div
          className={`px-6 py-2 ${
            complianceTab === 4 ? "bg-[#05123A] text-white" : "bg-gray-300"
          } cursor-pointer`}
          onClick={() => {
            setComplianceType("nist_800_53");
            setComplianceTab(4);
          }}
        >
          NIST
        </div>{" "}
        <div
          className={`px-6 py-2 ${
            complianceTab === 5 ? "bg-[#05123A] text-white" : "bg-gray-300"
          } cursor-pointer`}
          onClick={() => {
            setComplianceType("tsc");
            setComplianceTab(5);
          }}
        >
          TSC
        </div>
      </div>
    );
  };

  const compliancePanels = () => {
    const colors = [
      "rgba(96, 92, 166, 0.6)", // darker light purple
      "rgba(90, 162, 117, 0.6)", // darker light green
      "rgba(195, 138, 38, 0.6)", // darker yellow-orange
      "rgba(195, 78, 26, 0.6)", // darker soft orange
      "rgba(114, 172, 58, 0.6)", // darker lime green
      "rgba(26, 95, 195, 0.5)", // darker soft blue
      "rgba(215, 65, 140, 0.5)", // darker pastel pink
      "rgba(136, 45, 171, 0.5)", // darker light violet
    ];

    const versions = {};
    complianceData.forEach((entry) => {
      const timestamp = new Date(entry.time).getTime();
      Object.entries(entry).forEach(([key, value]) => {
        if (key !== "time") {
          if (!versions[key]) versions[key] = [];
          versions[key].push({
            x: timestamp,
            y: value,
            z: value,
          });
        }
      });
    });

    const complianceKeys = lastComplianceData.map((d) => d.complianceKey);
    const systems = Array.from(
      new Set(
        lastComplianceData.flatMap((item) =>
          Object.keys(item).filter((k) => k !== "complianceKey")
        )
      )
    );

    const matrix = systems.map((system) =>
      complianceKeys.map((complianceKey) => {
        const item = lastComplianceData.find(
          (d) => d.complianceKey === complianceKey
        );
        return item?.[system] ?? 0;
      })
    );

    const allValues = matrix.flat();
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    const getColor = (value) => {
      if (value === 0) return "#eee"; // Neutral for zero
      const ratio = (value - min) / (max - min || 1);

      // Define RGB for light and dark shades
      const light = { r: 224, g: 240, b: 237 }; // #E0F0ED
      const dark = { r: 46, g: 94, b: 87 }; // #2E5E57

      // Interpolate between light and dark based on ratio
      const r = Math.round(light.r + (dark.r - light.r) * ratio);
      const g = Math.round(light.g + (dark.g - light.g) * ratio);
      const b = Math.round(light.b + (dark.b - light.b) * ratio);

      return `rgb(${r},${g},${b})`;
    };

    const cellSize = 120;
    const width = complianceKeys.length * cellSize;
    const height = systems.length * cellSize;

    // Create discrete ranges
    const numBins = 5; // Change for more/less shades
    const rangeSize = (max - min) / numBins;

    const bins = Array.from({ length: numBins }, (_, i) => {
      const start = Math.round(min + i * rangeSize);
      const end = Math.round(i === numBins - 1 ? max : start + rangeSize);
      return { start, end, color: getColor(start) };
    });

    return (
      <div className="relative w-full text-[0.5rem] xl:text-[1rem]">
        {chartData !== "error" && chartData?.length > 0 ? (
          <>
            <div className="flex justify-center text-xl">
              Top Compliance requirements
            </div>

            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart>
                <CartesianGrid />
                <XAxis
                  type="number"
                  dataKey="x"
                  name="Time"
                  tickFormatter={(unixTime) =>
                    new Date(unixTime).toLocaleTimeString()
                  }
                  domain={["auto", "auto"]}
                />
                <YAxis type="number" dataKey="y" name="Count" />
                <ZAxis
                  type="number"
                  dataKey="z"
                  range={[100, 4000]}
                  name="Count"
                />
                <Tooltip
                  cursor={{ strokeDasharray: "3 3" }}
                  formatter={(value, name) => [
                    value,
                    name === "x" ? "Time" : name,
                  ]}
                  labelFormatter={(label) =>
                    `Time: ${new Date(label).toLocaleString()}`
                  }
                />
                <Legend />
                {Object.keys(versions).map((ver, idx) => (
                  <Scatter
                    key={ver}
                    name={ver}
                    data={versions[ver]}
                    fill={colors[idx % colors.length]}
                  />
                ))}
              </ScatterChart>
            </ResponsiveContainer>

            <div className="w-full h-96 p-4 mt-12">
              <h2 className="text-xl mb-4 text-center">
                Requirements by agent
              </h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={lastComplianceData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="complianceKey" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {systems.map((system, idx) => (
                    <Bar
                      key={system}
                      dataKey={system}
                      stackId="a"
                      fill={colors[idx % colors.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="w-full h-96 p-4 mt-12">
              <h2 className="text-xl mb-4 text-center">Last Alerts</h2>
              <svg width={width + 350} height={height + 100}>
                {bins.map((bin, i) => (
                  <g key={i}>
                    <rect
                      x={width + 240}
                      y={40 + i * 30}
                      width={20}
                      height={20}
                      fill={bin.color}
                      stroke="#ccc"
                    />
                    <text
                      x={width + 275}
                      y={55 + i * 30}
                      fontSize="12"
                      textAnchor="start"
                    >
                      {bin.start} - {bin.end}
                    </text>
                  </g>
                ))}

                <text
                  x={width + 275}
                  y={20}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="bold"
                >
                  Value Range
                </text>

                {complianceKeys.map((key, i) => (
                  <text
                    key={key}
                    x={i * cellSize + 250}
                    y={20}
                    textAnchor="middle"
                    fontSize="12"
                  >
                    {key}
                  </text>
                ))}

                {systems.map((sys, i) => (
                  <text
                    key={sys}
                    x={185}
                    y={i * cellSize + 70}
                    fontSize="12"
                    textAnchor="end"
                  >
                    {sys}
                  </text>
                ))}

                {matrix.map((row, y) =>
                  row.map((value, x) => (
                    <g key={`${x}-${y}`}>
                      <rect
                        x={x * cellSize + 200}
                        y={y * cellSize + 40}
                        width={cellSize}
                        height={cellSize / 2}
                        fill={getColor(value)}
                        stroke="#ccc"
                      />
                      <title>
                        {`System: ${systems[y]}\nCompliance Key: ${complianceKeys[x]}\nValue: ${value}`}
                      </title>
                    </g>
                  ))
                )}
              </svg>
            </div>
          </>
        ) : (
          <div className="flex justify-center items-center text-red-400">
            No Data Found!
          </div>
        )}
      </div>
    );
  };

  const chartData =
    complianceData !== "error" && complianceData?.length > 0 && complianceData;

  return (
    <>
      <UserNavbar />

      <div className="px-4 lg:w-[80vw] xl:w-[85vw] lg:absolute lg:right-0 lg:px-6 xl:absolute xl:right-0 xl:px-16 xl:pt-20 xl:pb-12 h-full">
        {showComplianceTabs()}
        {compliancePanels()}
      </div>
    </>
  );
};

export default Compliance;
